import { Input, OutsideClick } from '@upsales/components';
import { InputProps } from '@upsales/components/Input/Input';
import BemClass from '@upsales/components/Utils/bemClass';
import ContactAttributes from 'App/babel/attributes/ContactAttributes';
import Lookup from 'Resources/Lookup';
import _ from 'lodash';
import React, { useRef, useState } from 'react';
import { useSelector } from '../../hooks';

type Props = Omit<InputProps, 'onChange'> & {
	onChange: (value: string) => void;
};

export const TitleTypeahead = ({ value, onChange, ...props }: Props) => {
	const classes = new BemClass('TitleTypeahead');
	const typeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const customerId = useSelector(({ App }) => App.customerId);
	const [result, setResult] = useState<string[]>([]);
	const drilldownRef = useRef<HTMLUListElement>(null);

	const titleLookup = function (term: string) {
		if (term && term.length) {
			if (typeTimer.current) {
				clearTimeout(typeTimer.current);
			}

			typeTimer.current = setTimeout(async function () {
				const res = await Lookup.customer(customerId)
					.setType(Lookup.allTypes.CONTACT)
					.setCaseSensitive()
					.findEnd(ContactAttributes.title, term);

				setResult(_.unique(_.pluck(res.data, 'value').map((value: string) => value.trim())));
			}, 200);
		}
	};

	const onSelect = (title: string) => {
		onChange(title);
		setResult([]);
	};

	const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (drilldownRef.current && !event.shiftKey && ['Enter', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
			event.preventDefault();
			let activeElement = drilldownRef.current.querySelector<HTMLLIElement>('.active');

			if (!activeElement) {
				activeElement = drilldownRef.current.querySelector('li');
				activeElement?.classList.add('active');

				return;
			}

			let value: string, nextElement: HTMLLIElement | null;

			switch (event.key) {
				case 'Enter':
					value = activeElement.textContent || '';
					onSelect(value);
					break;
				case 'ArrowUp':
					nextElement = activeElement.previousElementSibling as HTMLLIElement;

					if (nextElement) {
						activeElement.classList.remove('active');
						nextElement.classList.add('active');
					} else {
						activeElement.classList.remove('active');
						drilldownRef.current.querySelector('li:last-child')?.classList.add('active');
					}
					break;
				case 'ArrowDown':
					nextElement = activeElement.nextElementSibling as HTMLLIElement;

					if (nextElement) {
						activeElement.classList.remove('active');
						nextElement.classList.add('active');
					} else {
						activeElement.classList.remove('active');
						drilldownRef.current.querySelector('li')?.classList.add('active');
					}
					break;
			}
		}
	};

	return (
		<OutsideClick targetClass={classes.b()} outsideClick={() => drilldownRef.current?.classList.remove('visible')}>
			<div className={classes.b()}>
				<Input
					className="up-type-ahead-wrapper"
					value={value}
					onFocus={() => drilldownRef.current?.classList.add('visible')}
					onChange={e => {
						titleLookup(e.target.value);
						onChange(e.target.value);
					}}
					onKeyDown={onKeyDown}
					{...props}
				/>
				<ul ref={drilldownRef} className={'up-type-ahead-items'}>
					{result.map(title => (
						<li key={title} className="type-ahead-item" onClick={() => onSelect(title)}>
							{title}
						</li>
					))}
				</ul>
			</div>
		</OutsideClick>
	);
};
